import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import CredentialExpiryAlert from "../../../components/dr/CredentialExpiryAlert";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";

const STATUS_TABS = [
  { value: "pending",  label: "Pending review" },
  { value: "approved", label: "Approved" },
  { value: "all",      label: "All live deals" },
];

function DRDeals() {
  // An oversight (Platform Manager) session reviews deals too, but it holds no
  // reviewer credential, so the credential banner is not its business.
  const { canDecide } = useRole();
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    drApi
      .deals({ status, page, limit })
      .then((res) => {
        if (!live) return;
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [status, page, limit]);

  const subtitle = (
    <>
      <span className="mono">{total}</span>
      <span>deal{total === 1 ? "" : "s"} matching "{status}"</span>
      <span>·</span>
      <span className="mono">page {page} of {Math.max(totalPages, 1)}</span>
    </>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Deals & allocations" subtitle={subtitle} />

      {canDecide && <CredentialExpiryAlert />}

      <div className="toolbar">
        <div className="toolbar__pills">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`toolbar__pill${status === t.value ? " toolbar__pill--on" : ""}`}
              onClick={() => { setStatus(t.value); setPage(1); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No deals match this filter.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Business</th>
                  <th>Security type</th>
                  <th className="r">Pending SLAs</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const pending = Number(r.pending_allocation_slas || 0);
                  return (
                    <tr key={r.id}>
                      <td>
                        <div>{r.corporation_legal_name || `Campaign #${r.id}`}</div>
                        <div className="tbl-id">ID {r.id}</div>
                      </td>
                      <td className="muted">{r.business || "—"}</td>
                      <td className="muted">{r.security_type || "—"}</td>
                      <td className="r">
                        <span className={`tbl-status ${pending > 0 ? "s-rev" : "s-mute"}`}>
                          {pending}
                        </span>
                      </td>
                      <td className="muted">
                        {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
    </PageShell>
  );
}

export default DRDeals;
